import React, { useState, useEffect } from "react";
import {
	Row,
	Col,
	Card,
	Badge,
	Button,
	Progress,
	Spin,
	Alert,
	Table,
	Tag,
	Space,
	Typography,
	Modal,
	message,
	Tabs,
} from "antd";
import {
	BookOutlined,
	CalendarOutlined,
	ClockCircleOutlined,
	CheckCircleOutlined,
	EyeOutlined,
	PlayCircleOutlined,
	VideoCameraOutlined,
	FileDoneOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useAxios from "../../utilities/useAxios";
import { formatDate, formatCurrency } from "../../utilities/Utils";
import PageTitle from "../PageTitle";

const { Title, Text } = Typography;

const MyEnrollments = ({ onEnrollmentUpdate }) => {
	const [enrollments, setEnrollments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selectedEnrollment, setSelectedEnrollment] = useState(null);
	const [showDetailModal, setShowDetailModal] = useState(false);
	const [activeTab, setActiveTab] = useState("active");
	const [activeCount, setActiveCount] = useState(0);
	const [finishedCount, setFinishedCount] = useState(0);
	const navigate = useNavigate();

	useEffect(() => {
		fetchEnrollments(activeTab);
		// Fetch counts for both tabs
		fetchCounts();
	}, [activeTab]);

	const fetchCounts = async () => {
		try {
			const [activeResponse, finishedResponse] =
				await Promise.all([
					useAxios.get(
						"/cma/enrollments/?status=active"
					),
					useAxios.get(
						"/cma/enrollments/?status=finished"
					),
				]);
			setActiveCount(activeResponse.data.length);
			setFinishedCount(finishedResponse.data.length);
		} catch (error) {
			console.error(
				"Error fetching enrollment counts:",
				error
			);
		}
	};

	const fetchEnrollments = async (status = "active") => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				`/cma/enrollments/?status=${status}`
			);
			setEnrollments(response.data);
			setError(null);
		} catch (error) {
			console.error("Error fetching enrollments:", error);
			setError("수강 신청 내역을 불러오는데 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const handleViewDetail = (enrollment) => {
		setSelectedEnrollment(enrollment);
		setShowDetailModal(true);
	};

	const handleCloseModal = () => {
		setShowDetailModal(false);
		setSelectedEnrollment(null);
	};

	const handleShowVideos = (course, isFinished) => {
		if (isFinished) {
			message.warning("종료된 강의는 영상을 볼 수 없습니다.");
			return;
		}
		navigate(`/cma/courses/${course.id}/videos`);
	};

	const handleGoExam = (courseId) => {
		navigate(`/cma/courses/${courseId}/exam`);
	};

	const getProgressColor = (progress) => {
		if (progress >= 80) return "#52c41a";
		if (progress >= 50) return "#faad14";
		return "#2457e6";
	};

	const getResultColor = (result) => {
		switch (result) {
			case "수료":
				return "green";
			case "미수료":
				return "orange";
			default:
				return "default";
		}
	};

	const getColumns = (isFinished) => [
		{
			title: "강의명",
			dataIndex: ["course", "name"],
			key: "course_name",
			render: (text, record) => (
				<Button
					type="link"
					onClick={() => handleViewDetail(record)}
					style={{ padding: 0, height: "auto" }}>
					{text}
				</Button>
			),
		},
		{
			title: "책명",
			dataIndex: ["course", "book", "title"],
			key: "book_name",
			render: (bookTitle) => (
				<Space>
					<BookOutlined />
					{bookTitle}
				</Space>
			),
		},
		{
			title: "수강 신청일",
			dataIndex: "payment_date",
			key: "payment_date",
			render: (date) => (
				<Space>
					<CalendarOutlined />
					{formatDate(date)}
				</Space>
			),
		},
		{
			title: "진도율",
			dataIndex: "progress",
			key: "progress",
			render: (progress) => (
				<div style={{ width: "100px" }}>
					<Progress
						percent={progress}
						size="small"
						strokeColor={getProgressColor(
							progress
						)}
						format={(percent) =>
							`${percent}%`
						}
					/>
				</div>
			),
		},
		{
			title: "결과",
			dataIndex: "result",
			key: "result",
			render: (result) => (
				<Tag color={getResultColor(result)}>
					{result}
				</Tag>
			),
		},
		{
			title: "결제 상태",
			dataIndex: "is_paid",
			key: "is_paid",
			render: (isPaid) => (
				<Badge
					status={isPaid ? "success" : "error"}
					text={isPaid ? "결제완료" : "미결제"}
				/>
			),
		},
		{
			title: "강의 영상",
			key: "videos",
			render: (_, record) => (
				<Button
					type="primary"
					size="small"
					icon={<VideoCameraOutlined />}
					disabled={record.is_finished}
					onClick={() =>
						handleShowVideos(
							record.course,
							record.is_finished
						)
					}>
					영상 보기
				</Button>
			),
		},
		{
			title: "시험",
			key: "exam",
			render: (_, record) => {
				if (record.exam_submitted) {
					return (
						<Space direction="vertical" size={0}>
							<Tag color={record.exam_passed ? "green" : "red"}>
								{record.exam_passed ? "합격" : "불합격"}
							</Tag>
							<Text>점수: {record.exam_score}점</Text>
						</Space>
					);
				}
				if (!record.exam_available) {
					return <Text type="secondary">응시 불가</Text>;
				}
				return (
					<Button
						type="primary"
						size="small"
						icon={<FileDoneOutlined />}
						onClick={() => handleGoExam(record.course.id)}>
						시험 응시
					</Button>
				);
			},
		},
		{
			title: "자세히",
			key: "actions",
			render: (_, record) => (
				<Space>
					<Button
						type="primary"
						size="small"
						icon={<EyeOutlined />}
						onClick={() =>
							handleViewDetail(record)
						}>
						보기
					</Button>
				</Space>
			),
		},
	];

	if (loading) {
		return (
			<div style={{ textAlign: "center", padding: "50px" }}>
				<Spin size="large" />
			</div>
		);
	}

	const tabItems = [
		{
			key: "active",
			label: `진행 중 (${activeCount})`,
			children: (
				<Table
					columns={getColumns(false)}
					dataSource={enrollments}
					rowKey="id"
					pagination={{
						pageSize: 10,
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) =>
							`${range[0]}-${range[1]} / 총 ${total}개`,
					}}
				/>
			),
		},
		{
			key: "finished",
			label: `종료됨 (${finishedCount})`,
			children: (
				<Table
					columns={getColumns(true)}
					dataSource={enrollments}
					rowKey="id"
					pagination={{
						pageSize: 10,
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) =>
							`${range[0]}-${range[1]} / 총 ${total}개`,
					}}
				/>
			),
		},
	];

	return (
		<div>
			<PageTitle title="기독사관학교 - 수강 신청 내역" />
			{error && (
				<Alert
					message="오류"
					description={error}
					type="error"
					showIcon
					className="mb-3"
				/>
			)}

			<Card>
				<Tabs
					activeKey={activeTab}
					onChange={setActiveTab}
					items={tabItems}
				/>
			</Card>

			{/* Enrollment Detail Modal */}
			<Modal
				title="수강 신청 상세 정보"
				open={showDetailModal}
				onCancel={handleCloseModal}
				footer={[
					<Button
						key="close"
						onClick={handleCloseModal}>
						닫기
					</Button>,
				]}
				width={700}>
				{selectedEnrollment && (
					<div>
						<Title level={4}>
							{
								selectedEnrollment
									.course
									?.name
							}
						</Title>
						<Row gutter={[16, 16]}>
							<Col span={12}>
								<Text strong>
									책명:
								</Text>
								<br />
								<Text>
									{
										selectedEnrollment
											.course
											?.book
											?.title
									}
								</Text>
							</Col>
							<Col span={12}>
								<Text strong>
									결과:
								</Text>
								<br />
								<Tag
									color={getResultColor(
										selectedEnrollment.result
									)}>
									{
										selectedEnrollment.result
									}
								</Tag>
							</Col>
							<Col span={12}>
								<Text strong>시험 결과:</Text>
								<br />
								{selectedEnrollment.exam_submitted ? (
									<Space direction="vertical" size={0}>
										<Tag
											color={
												selectedEnrollment.exam_passed
													? "green"
													: "red"
											}>
											{selectedEnrollment.exam_passed
												? "합격"
												: "불합격"}
										</Tag>
										<Text>
											점수:{" "}
											{selectedEnrollment.exam_score}
											점
										</Text>
									</Space>
								) : (
									<Text type="secondary">
										미응시
									</Text>
								)}
							</Col>
							<Col span={12}>
								<Text strong>
									수강
									신청일:
								</Text>
								<br />
								<Text>
									{formatDate(
										selectedEnrollment.payment_date
									)}
								</Text>
							</Col>
							<Col span={12}>
								<Text strong>
									진도율:
								</Text>
								<br />
								<Progress
									percent={
										selectedEnrollment.progress
									}
									strokeColor={getProgressColor(
										selectedEnrollment.progress
									)}
									format={(
										percent
									) =>
										`${percent}%`
									}
								/>
							</Col>
							<Col span={12}>
								<Text strong>
									결제
									상태:
								</Text>
								<br />
								<Badge
									status={
										selectedEnrollment.is_paid
											? "success"
											: "error"
									}
									text={
										selectedEnrollment.is_paid
											? "결제완료"
											: "미결제"
									}
								/>
							</Col>
							<Col span={12}>
								<Text strong>
									결제
									금액:
								</Text>
								<br />
								<Text>
									{formatCurrency(
										selectedEnrollment.payment_amount
									)}
								</Text>
							</Col>
							{selectedEnrollment
								.course
								?.type && (
								<Col span={12}>
									<Text
										strong>
										강의
										유형:
									</Text>
									<br />
									<Text>
										{
											selectedEnrollment
												.course
												.type
										}
									</Text>
								</Col>
							)}
							{selectedEnrollment
								.course
								?.start_date && (
								<Col span={12}>
									<Text
										strong>
										강의
										시작일:
									</Text>
									<br />
									<Text>
										{formatDate(
											selectedEnrollment
												.course
												.start_date
										)}
									</Text>
								</Col>
							)}
							<Col span={24}>
								<Space style={{ marginTop: "16px" }}>
									<Button
										type="primary"
										icon={
											<VideoCameraOutlined />
										}
										disabled={
											selectedEnrollment.is_finished
										}
										onClick={() => {
											if (
												!selectedEnrollment.is_finished
											) {
												handleShowVideos(
													selectedEnrollment.course,
													selectedEnrollment.is_finished
												);
												handleCloseModal();
											} else {
												message.warning(
													"종료된 강의는 영상을 볼 수 없습니다."
												);
											}
										}}>
										강의 영상 보기
									</Button>
									{selectedEnrollment.exam_available &&
										!selectedEnrollment.exam_submitted && (
											<Button
												type="primary"
												icon={
													<FileDoneOutlined />
												}
												onClick={() => {
													handleGoExam(
														selectedEnrollment.course.id
													);
													handleCloseModal();
												}}>
												시험 응시
											</Button>
										)}
								</Space>
							</Col>
						</Row>
					</div>
				)}
			</Modal>
		</div>
	);
};

export default MyEnrollments;
